/**
 * useAngebote — Client-Hook für die Werkstatt-Angebote API (Issue #396)
 *
 * Wraps fetch('/api/angebote', { action: '...' }) für alle CRUD-Operationen.
 * Hält die Angebots-Liste im lokalen State und bietet Methoden für
 * create, update, updateStatus, addPosition, updatePosition, removePosition, remove.
 */

import { useCallback, useEffect, useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type AngebotStatus = 'entwurf' | 'versendet' | 'angenommen' | 'abgelehnt' | 'abgelaufen' | 'in_auftrag';
export type PositionsTyp = 'arbeit' | 'ersatzteil' | 'lack' | 'material';

export interface Angebot {
  id: string;
  angebot_nr: string;
  betrieb_id: string;
  marke_id: string | null;
  kunde_id: string | null;
  fahrzeug_id: string | null;
  akte_id: string | null;
  netto_betrag: number;
  mwst_satz: number;
  brutto_betrag: number;
  gueltig_bis: string | null;
  status: AngebotStatus;
  pdf_url: string | null;
  versendet_am: string | null;
  angenommen_am: string | null;
  auftrag_id: string | null;
  erstellt_von: string | null;
  created_at: string;
  updated_at: string;
}

export interface AngebotPosition {
  id: string;
  angebot_id: string;
  position_nr: number;
  bezeichnung: string;
  menge: number;
  einheit: string;
  einzelpreis_netto: number;
  positions_typ: PositionsTyp | null;
  lieferant: string | null;
  bestellnummer: string | null;
  created_at: string;
}

// ─── API Helper ──────────────────────────────────────────────────────────────

async function callApi<T>(action: string, body: Record<string, unknown> = {}): Promise<T> {
  const res = await fetch('/api/angebote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...body }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || `API error ${res.status}`);
  }
  return data as T;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAngebote(betriebId: string | undefined) {
  const [angebote, setAngebote] = useState<Angebot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AngebotStatus | 'alle'>('alle');
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    if (!betriebId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const body: Record<string, unknown> = { betrieb_id: betriebId };
        if (statusFilter !== 'alle') body.status = statusFilter;
        const data = await callApi<{ angebote: Angebot[] }>('list', body);
        if (!cancelled) setAngebote(data.angebote ?? []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Fehler beim Laden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [betriebId, statusFilter, refreshKey]);

  const create = useCallback(
    (input: {
      kunde_id?: string;
      fahrzeug_id?: string;
      akte_id?: string;
      mwst_satz?: number;
      gueltig_bis?: string;
      erstellt_von?: string;
    }) => callApi<{ success: boolean; angebot: Angebot }>('create', { betrieb_id: betriebId, ...input }),
    [betriebId],
  );

  const update = useCallback(
    (id: string, patch: Partial<Pick<Angebot, 'marke_id' | 'kunde_id' | 'fahrzeug_id' | 'akte_id' | 'mwst_satz' | 'gueltig_bis' | 'pdf_url'>>) =>
      callApi<{ success: boolean; angebot: Angebot }>('update', { id, ...patch }),
    [],
  );

  const updateStatus = useCallback(
    (id: string, status: AngebotStatus) =>
      callApi<{ success: boolean; angebot: Angebot }>('updateStatus', { id, status }),
    [],
  );

  const addPosition = useCallback(
    (angebot_id: string, pos: {
      bezeichnung: string;
      menge?: number;
      einheit?: string;
      einzelpreis_netto?: number;
      positions_typ?: PositionsTyp;
      lieferant?: string;
      bestellnummer?: string;
    }) => callApi<{ success: boolean; position: AngebotPosition }>('addPosition', { angebot_id, ...pos }),
    [],
  );

  const updatePosition = useCallback(
    (id: string, patch: Partial<Omit<AngebotPosition, 'id' | 'angebot_id' | 'created_at'>>) =>
      callApi<{ success: boolean; position: AngebotPosition }>('updatePosition', { id, ...patch }),
    [],
  );

  const removePosition = useCallback(
    (id: string) => callApi<{ success: boolean }>('removePosition', { id }),
    [],
  );

  const remove = useCallback(
    (id: string) => callApi<{ success: boolean }>('remove', { id }),
    [],
  );

  const getById = useCallback(
    (id: string) => callApi<{ angebot: Angebot; positionen: AngebotPosition[] }>('byId', { id }),
    [],
  );

  return {
    angebote,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    refresh,
    create,
    update,
    updateStatus,
    addPosition,
    updatePosition,
    removePosition,
    remove,
    getById,
  };
}

// ─── Client-seitige Summenberechnung ─────────────────────────────────────────

export interface LocalPosition {
  id?: string;
  bezeichnung: string;
  menge: number;
  einheit: string;
  einzelpreis_netto: number;
  positions_typ: PositionsTyp | null;
}

export function calcSummen(positionen: LocalPosition[], mwstSatz: number) {
  const netto = positionen.reduce((sum, p) => sum + p.menge * p.einzelpreis_netto, 0);
  const mwstBetrag = Math.round(netto * (mwstSatz / 100) * 100) / 100;
  const brutto = Math.round(netto * (1 + mwstSatz / 100) * 100) / 100;
  return { netto: Math.round(netto * 100) / 100, mwstBetrag, brutto };
}
